import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download,
  MoreVertical,
  User,
  BookOpen,
  Calendar,
  Eye,
  Edit,
  Pause,
  Play,
  X,
  CheckCircle,
  Clock,
  AlertCircle,
  Printer,
  Hash,
  FileSpreadsheet
} from 'lucide-react';
import * as XLSX from 'xlsx';

import MatriculaCreate from '../components/MatriculaCreate';
import ValidacaoFeedback from '../components/ValidacaoFeedback';
import ModalError from '../components/ModalError';
import Pagination from '../components/Pagination';
import AjustarMatriculaModal from '../components/AjustarMatriculaModal';
import { matriculaService } from '../services/matriculaService';
import cursoService from '../services/cursoService';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';

const MESES_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const PREPOSICOES_PT = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'a', 'o', 'as', 'os', 'em', 'no', 'na', 'nos', 'nas', 'ao', 'aos', 'com', 'por', 'para', 'sem', 'sob', 'ante', 'até']);

const toTitleCase = (str) => {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      if (index !== 0 && PREPOSICOES_PT.has(word)) return word;
      return word.replace(/^-?(\S)/, ch => ch.toUpperCase());
    })
    .join(' ');
};

const MatriculasList = () => {
  // Hook de navegação
  const navigate = useNavigate();
  
  // Hook de autenticação e permissões
  const { hasPermission } = useAuth();
  
  // Estados principais
  const [matriculas, setMatriculas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // Estados de UI
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMatricula, setSelectedMatricula] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionData, setActionData] = useState({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAjustarMatriculaModal, setShowAjustarMatriculaModal] = useState(false);

  // Estados para seleção em massa de históricos
  const [selectedHistoricos, setSelectedHistoricos] = useState([]);
  const [printingMass, setPrintingMass] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Estados de filtros e busca
  // Permite chegar nesta tela com filtros pré-aplicados via querystring
  // (ex.: /matriculas?dataInicio=2026-04-26&dataFim=2026-05-26 — drill-down
  // do KPI "Novas matrículas" do Dashboard Admin).
  const [searchParams] = useSearchParams();
  const [filtros, setFiltros] = useState({
    busca: searchParams.get('busca') || '',
    status: searchParams.get('status') || '',
    curso: searchParams.get('curso') || '',
    dataInicio: searchParams.get('dataInicio') || '',
    dataFim: searchParams.get('dataFim') || '',
    page: 1,
    limit: 20
  });

  // Estado para lista de cursos
  const [cursos, setCursos] = useState([]);

  // Estado para debounce da busca
  const [buscaTemp, setBuscaTemp] = useState('');
  
  // Ref para controlar execução do debounce
  const debounceRef = useRef(null);
  const executandoBuscaRef = useRef(false);

  // Estados de paginação
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Estatísticas
  const [estatisticas, setEstatisticas] = useState({
    total: 0,
    ativas: 0,
    trancadas: 0,
    concluidas: 0,
    canceladas: 0
  });

  // Carregar matrículas
  const carregarMatriculas = useCallback(async (filtrosParaUsar) => {
    setLoading(true);
    setError(null);

    try {
      const response = await matriculaService.listarMatriculas(filtrosParaUsar);
      
      if (response.success) {
        setMatriculas(response.data || []);
        
        // Mapear corretamente os campos de paginação da API
        if (response.pagination) {
          const novaPaginacao = {
            currentPage: Number(response.pagination.page) || 1,
            totalPages: Number(response.pagination.totalPages) || 1,
            totalItems: Number(response.pagination.total) || 0,
            itemsPerPage: Number(response.pagination.limit) || 20
          };
          setPagination(novaPaginacao);
        }
        
        // Não limpar estatísticas ao filtrar - elas são carregadas separadamente
        // setEstatisticas(response.estatisticas || {});
      } else {
        setError(response.error || 'Erro ao carregar matrículas');
      }
    } catch (error) {
      console.error('Erro ao carregar matrículas:', error);
      setError('Erro inesperado ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []); // Sem dependências pois usa apenas setters de estado

  // Carregar estatísticas
  const carregarEstatisticas = useCallback(async () => {
    try {
      console.log('🔄 Carregando estatísticas...');
      const response = await matriculaService.obterEstatisticas();
      
      console.log('📊 Resposta das estatísticas:', response);
      
      if (response.success && response.data.estatisticas) {
        console.log('✅ Estatísticas recebidas:', response.data.estatisticas);
        setEstatisticas(response.data.estatisticas);
      } else {
        console.log('❌ Falha ao carregar estatísticas:', response.error);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  }, []);

  // Carregar cursos
  const carregarCursos = useCallback(async () => {
    try {
      const response = await cursoService.list({ limit: 1000 });
      if (response.success && response.data) {
        setCursos(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
    }
  }, []);

  // Carregar dados inicial
  useEffect(() => {
    // Mapear filtros iniciais
    const filtrosAPI = {
      page: 1,
      limit: 20,
      busca: '',
      status: '',
      curso: ''
    };
    
    carregarMatriculas(filtrosAPI);
    carregarEstatisticas();
    carregarCursos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce para busca - versão segura contra loops
  useEffect(() => {
    // Limpar timeout anterior se existir
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Evitar execução dupla
    if (executandoBuscaRef.current) {
      return;
    }
    
    debounceRef.current = setTimeout(() => {
      setFiltros(prevFiltros => {
        // Só executar se realmente mudou e não está executando
        if (buscaTemp !== prevFiltros.busca && !executandoBuscaRef.current) {
          executandoBuscaRef.current = true;
          
          const novosFiltros = {
            ...prevFiltros,
            busca: buscaTemp,
            page: 1
          };
          
          // Chamar API de forma controlada
          const filtrosAPI = {
            ...novosFiltros,
            dataMatriculaInicio: novosFiltros.dataInicio,
            dataMatriculaFim: novosFiltros.dataFim
          };
          delete filtrosAPI.dataInicio;
          delete filtrosAPI.dataFim;
          
          // Usar Promise para controlar execução
          carregarMatriculas(filtrosAPI).finally(() => {
            executandoBuscaRef.current = false;
          });
          
          return novosFiltros;
        }
        return prevFiltros;
      });
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaTemp]); // Removido carregarMatriculas das dependências

  // Atualizar filtros - versão simplificada
  const atualizarFiltros = useCallback((novosFiltros) => {
    setFiltros(prevFiltros => ({
      ...prevFiltros,
      ...novosFiltros,
      ...((!novosFiltros.hasOwnProperty('page') && !novosFiltros.hasOwnProperty('limit')) && { page: 1 })
    }));
    
    // Chamar API separadamente para evitar setState dentro de setState
    setTimeout(() => {
      setFiltros(filtrosAtuais => {
        const filtrosAPI = {
          ...filtrosAtuais,
          ...novosFiltros,
          ...((!novosFiltros.hasOwnProperty('page') && !novosFiltros.hasOwnProperty('limit')) && { page: 1 }),
          dataMatriculaInicio: filtrosAtuais.dataInicio,
          dataMatriculaFim: filtrosAtuais.dataFim
        };
        delete filtrosAPI.dataInicio;
        delete filtrosAPI.dataFim;
        
        carregarMatriculas(filtrosAPI);
        return filtrosAtuais; // Não modificar, só chamar API
      });
    }, 0);
  }, [carregarMatriculas]);

  // Limpar filtros
  const limparFiltros = useCallback(() => {
    const filtrosLimpos = {
      busca: '',
      status: '',
      curso: '',
      dataInicio: '',
      dataFim: '',
      page: 1,
      limit: 20
    };
    setFiltros(filtrosLimpos);
    setBuscaTemp(''); // Limpar busca temporária também
    
    // Executar em timeout para evitar problema de timing
    setTimeout(() => {
      const filtrosAPI = {
        ...filtrosLimpos,
        dataMatriculaInicio: '',
        dataMatriculaFim: ''
      };
      delete filtrosAPI.dataInicio;
      delete filtrosAPI.dataFim;
      carregarMatriculas(filtrosAPI);
    }, 0);
  }, [carregarMatriculas]);

  // Abrir modal de ação
  const abrirModalAcao = useCallback((acao, matricula) => {
    setSelectedMatricula(matricula);
    setActionType(acao);
    
    // Definir valores padrão baseados na ação
    const defaultData = {};
    if (acao === 'trancar') {
      defaultData.dataTrancamento = new Date().toISOString().split('T')[0];
    } else if (acao === 'concluir') {
      defaultData.dataConclusao = new Date().toISOString().split('T')[0];
    }
    
    setActionData(defaultData);
    setShowActionModal(true);
  }, []);

  // Fechar modal de ação
  const fecharModalAcao = useCallback(() => {
    setShowActionModal(false);
    setActionType(null);
    setSelectedMatricula(null);
    setActionData({});
  }, []);

  // Imprimir histórico acadêmico
  const imprimirHistorico = useCallback(async (matricula) => {
    try {
      setActionLoading(prev => ({ ...prev, [`historico_${matricula.id}`]: true }));
      
      const response = await matriculaService.imprimirHistorico(matricula.id);
      
      if (!response.success) {
        setError(response.error || 'Erro ao imprimir histórico');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Erro ao imprimir histórico:', error);
      setError('Erro inesperado ao imprimir histórico');
      setShowErrorModal(true);
    } finally {
      setActionLoading(prev => ({ ...prev, [`historico_${matricula.id}`]: false }));
    }
  }, []);

  // Ajustar número de matrícula
  const handleAjustarMatricula = async (idMatricula, novoNumero) => {
    try {
      const result = await matriculaService.ajustarNumeroMatricula(idMatricula, novoNumero);
      
      if (result.success) {
        alert(result.message);
        // Recarregar lista
        carregarMatriculas(filtros);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      throw error;
    }
  };

  // Funções de seleção em massa para históricos
  const matriculasElegiveis = useMemo(() => {
    return matriculas.filter(m => m.status === 'Concluída' || m.status === 'Concluida');
  }, [matriculas]);

  const toggleSelectHistorico = useCallback((matriculaId) => {
    setSelectedHistoricos(prev => {
      if (prev.includes(matriculaId)) {
        return prev.filter(id => id !== matriculaId);
      } else {
        return [...prev, matriculaId];
      }
    });
  }, []);

  const toggleSelectAllHistoricos = useCallback(() => {
    if (selectedHistoricos.length === matriculasElegiveis.length) {
      setSelectedHistoricos([]);
    } else {
      setSelectedHistoricos(matriculasElegiveis.map(m => m.id));
    }
  }, [selectedHistoricos.length, matriculasElegiveis]);

  const exportarExcelSelecionados = useCallback(async () => {
    if (selectedHistoricos.length === 0) {
      setError('Selecione ao menos uma matrícula concluída para exportar');
      setShowErrorModal(true);
      return;
    }
    setExportingExcel(true);
    try {
      const resultado = await matriculaService.exportarExcelConcluidos(selectedHistoricos);
      if (!resultado.success) {
        setError(resultado.error || 'Erro ao exportar Excel');
        setShowErrorModal(true);
        return;
      }
      if (!resultado.data || resultado.data.length === 0) {
        setError('Nenhuma matrícula selecionada corresponde aos cursos exportáveis (Básico, Médio, Avançado, Convalidação, Aconselhamento Pastoral, Capelania)');
        setShowErrorModal(true);
        return;
      }

      // "Básico" e "Médio" precisam ser exibidos como "Básico em Teologia"
      // / "Médio em Teologia" no certificado (já que o sistema cadastra
      // só "Básico" / "Médio"). Outros nomes (Aconselhamento Pastoral,
      // Capelania, Avançado, Convalidação) ficam como estão.
      const formatarNomeCurso = (nome) => {
        if (!nome) return '';
        const original = String(nome).trim();
        // Já contém "Teologia"? mantém como está.
        if (/teologia/i.test(original)) return original;
        const norm = original.toLowerCase();
        if (norm === 'básico' || norm === 'basico') return 'Básico em Teologia';
        if (norm === 'médio'  || norm === 'medio')  return 'Médio em Teologia';
        return original;
      };

      // Data de geração do Excel (data local, não UTC, para evitar que
      // exportação após as 21h vire o dia seguinte).
      const hoje = new Date();
      const impressaoDia = hoje.getDate();
      const impressaoMes = MESES_PT[hoje.getMonth()];
      const impressaoAno = hoje.getFullYear();

      const linhas = resultado.data.map(row => {
        const nascimento = row.dataNascimento ? new Date(row.dataNascimento) : null;
        const conclusao = row.dataConclusao ? new Date(row.dataConclusao) : null;
        return {
          'Nome': toTitleCase(row.nome),
          'Pai': toTitleCase(row.filiacaoPai),
          'Mae': toTitleCase(row.filiacaoMae),
          'RG': row.identidade || '',
          'Orgao_Exp': row.orgaoExpedidor || '',
          'dia_nascimento': nascimento ? nascimento.getUTCDate() : '',
          'mês_nascimento': nascimento ? MESES_PT[nascimento.getUTCMonth()] : '',
          'ano_nascimento': nascimento ? nascimento.getUTCFullYear() : '',
          'Natural': toTitleCase(row.naturalidade),
          'Curso': formatarNomeCurso(row.nomeCurso),
          'Conclusao_mes': conclusao ? MESES_PT[conclusao.getUTCMonth()] : '',
          'Conclusao_ano': conclusao ? conclusao.getUTCFullYear() : '',
          'Impressao_dia': impressaoDia,
          'Impressao_mes': impressaoMes,
          'Impressao_ano': impressaoAno,
          'Extensão': toTitleCase(row.nomeExtensao),
          'Registro': '',
          'Livro': '',
          'Fls': '',
        };
      });
      const ws = XLSX.utils.json_to_sheet(linhas);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Concluídos');
      XLSX.writeFile(wb, `matriculas-concluidas-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Erro ao exportar Excel:', err);
      setError(`Erro ao gerar Excel: ${err.message}`);
      setShowErrorModal(true);
    } finally {
      setExportingExcel(false);
    }
  }, [selectedHistoricos]);

  const imprimirHistoricosMassa = useCallback(async () => {
    if (selectedHistoricos.length === 0) {
      setError('Selecione ao menos uma matrícula concluída para impressão');
      setShowErrorModal(true);
      return;
    }

    setPrintingMass(true);

    try {
      const resultado = await matriculaService.imprimirHistoricosMassaPDF(selectedHistoricos);

      // Limpar seleção
      setSelectedHistoricos([]);

      if (!resultado.success) {
        setError(resultado.error || 'Erro ao gerar PDF em massa dos históricos');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Erro ao imprimir históricos em massa:', error);
      setError('Erro inesperado ao imprimir históricos');
      setShowErrorModal(true);
    } finally {
      setPrintingMass(false);
    }
  }, [selectedHistoricos]);

  // Fechar modal de erro
  const fecharModalErro = useCallback(() => {
    setShowErrorModal(false);
    setError(null);
  }, []);

  // Ações de matrícula
  const executarAcao = useCallback(async (acao, matriculaId, dadosAdicionais = {}) => {
    setActionLoading(prev => ({ ...prev, [matriculaId]: acao }));

    try {
      let response;
      
      switch (acao) {
        case 'trancar':
          response = await matriculaService.trancarMatricula(matriculaId, dadosAdicionais);
          break;
        case 'reativar':
          response = await matriculaService.reativarMatricula(matriculaId, dadosAdicionais);
          break;
        case 'concluir':
          response = await matriculaService.concluirMatricula(matriculaId, dadosAdicionais);
          break;
        case 'cancelar':
          response = await matriculaService.cancelarMatricula(matriculaId, dadosAdicionais.motivoCancelamento);
          break;
        default:
          throw new Error(`Ação não reconhecida: ${acao}`);
      }

      if (response.success) {
        // Recarregar lista e estatísticas usando filtros atuais
        setFiltros(filtrosAtuais => {
          const filtrosAPI = {
            ...filtrosAtuais,
            dataMatriculaInicio: filtrosAtuais.dataInicio,
            dataMatriculaFim: filtrosAtuais.dataFim
          };
          delete filtrosAPI.dataInicio;
          delete filtrosAPI.dataFim;
          
          // Chamar de forma assíncrona
          setTimeout(() => {
            carregarMatriculas(filtrosAPI);
            carregarEstatisticas();
          }, 0);
          
          return filtrosAtuais; // Não modificar os filtros
        });
        
        // Fechar modais
        setShowActionModal(false);
        setActionType(null);
        setActionData({});
        setSelectedMatricula(null);
        
        // Mostrar sucesso
        console.log(`${acao} executada com sucesso`);
        setError(null);
      } else {
        // Verificar se há erros de validação específicos
        if (response.validationErrors && Array.isArray(response.validationErrors)) {
          const validationMessages = response.validationErrors.map(error => error.msg || error.message).join('; ');
          setError(validationMessages);
          setShowErrorModal(true);
        } else {
          setError(response.error || `Erro ao executar ${acao}`);
          setShowErrorModal(true);
        }
      }
    } catch (error) {
      console.error(`Erro ao executar ${acao}:`, error);
      setError(`Erro inesperado ao executar ${acao}`);
      setShowErrorModal(true);
    } finally {
      setActionLoading(prev => ({ ...prev, [matriculaId]: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removido dependências para evitar loops

  // Callback para salvar nova matrícula
  const handleMatriculaSave = useCallback((novaMatricula) => {
    setShowCreateModal(false);
    // Recarregar lista com filtros atuais usando setState funcional
    setFiltros(filtrosAtuais => {
      const filtrosAPI = {
        ...filtrosAtuais,
        dataMatriculaInicio: filtrosAtuais.dataInicio,
        dataMatriculaFim: filtrosAtuais.dataFim
      };
      delete filtrosAPI.dataInicio;
      delete filtrosAPI.dataFim;
      
      // Chamar de forma assíncrona
      setTimeout(() => {
        carregarMatriculas(filtrosAPI);
      }, 0);
      
      return filtrosAtuais; // Não modificar os filtros
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removido carregarMatriculas para evitar loops

  // Callback para cancelar criação de matrícula  
  const handleMatriculaCancel = useCallback(() => {
    setShowCreateModal(false);
  }, []);
  // Formatar status para exibição
  const formatarStatus = useCallback((status) => {
    const statusConfig = {
      'Ativa': {
        color: 'text-green-800 bg-green-100',
        icon: CheckCircle,
        label: 'Ativa'
      },
      'Trancada': {
        color: 'text-yellow-800 bg-yellow-100',
        icon: Pause,
        label: 'Trancada'
      },
      'Concluida': {
        color: 'text-blue-800 bg-blue-100',
        icon: CheckCircle,
        label: 'Concluída'
      },
      'Cancelada': {
        color: 'text-red-800 bg-red-100',
        icon: X,
        label: 'Cancelada'
      }
    };

    const config = statusConfig[status] || {
      color: 'text-gray-800 bg-gray-100',
      icon: Clock,
      label: status
    };

    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  }, []);

  // Renderizar estatísticas
  const renderEstatisticas = useCallback(() => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <User className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500" />
          </div>
          <div className="ml-2 sm:ml-3 w-0 flex-1">
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total</dt>
              <dd className="text-base sm:text-lg font-semibold text-gray-900">{estatisticas.total || 0}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
          </div>
          <div className="ml-2 sm:ml-3 w-0 flex-1">
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Ativas</dt>
              <dd className="text-base sm:text-lg font-semibold text-green-600">{estatisticas.ativas || 0}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Pause className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500" />
          </div>
          <div className="ml-2 sm:ml-3 w-0 flex-1">
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Trancadas</dt>
              <dd className="text-base sm:text-lg font-semibold text-yellow-600">{estatisticas.trancadas || 0}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
          </div>
          <div className="ml-2 sm:ml-3 w-0 flex-1">
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Concluídas</dt>
              <dd className="text-base sm:text-lg font-semibold text-blue-600">{estatisticas.concluidas || 0}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 col-span-2 sm:col-span-1">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <X className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
          </div>
          <div className="ml-2 sm:ml-3 w-0 flex-1">
            <dl>
              <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Canceladas</dt>
              <dd className="text-base sm:text-lg font-semibold text-red-600">{estatisticas.canceladas || 0}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  ), [estatisticas]);

  // Renderizar filtros
  const renderFiltros = useCallback(() => (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 transition-all duration-200 ${
      showFilters ? 'block' : 'hidden'
    }`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={filtros.status}
            onChange={(e) => atualizarFiltros({ status: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          >
            <option value="">Todos</option>
            <option value="Ativa">Ativas</option>
            <option value="Trancada">Trancadas</option>
            <option value="Concluida">Concluídas</option>
            <option value="Cancelada">Canceladas</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Curso</label>
          <select
            value={filtros.curso}
            onChange={(e) => atualizarFiltros({ curso: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          >
            <option value="">Todos os cursos</option>
            {cursos.map(curso => (
              <option key={curso.idCurso} value={curso.nome}>
                {curso.nome}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
          <input
            type="date"
            value={filtros.dataInicio}
            onChange={(e) => atualizarFiltros({ dataInicio: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
          <input
            type="date"
            value={filtros.dataFim}
            onChange={(e) => atualizarFiltros({ dataFim: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          />
        </div>

        <div className="flex items-end sm:col-span-2 lg:col-span-1">
          <button
            onClick={limparFiltros}
            className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <span className="hidden sm:inline">Limpar Filtros</span>
            <span className="sm:hidden">Limpar</span>
          </button>
        </div>
      </div>
    </div>
  ), [showFilters, filtros, atualizarFiltros, limparFiltros]);

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Matrículas</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Gerencie as matrículas dos alunos nos cursos
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center px-3 sm:px-4 py-2 border rounded-md transition-colors text-sm ${
                showFilters 
                  ? 'border-blue-500 text-blue-700 bg-blue-50' 
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Filtros</span>
              <span className="sm:hidden">Filtrar</span>
            </button>

            {/* Botão de exportação Excel em massa */}
            {selectedHistoricos.length > 0 && (
              <button
                onClick={exportarExcelSelecionados}
                disabled={exportingExcel}
                className="flex items-center justify-center px-3 sm:px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingExcel ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Exportar Excel ({selectedHistoricos.length})</span>
                    <span className="sm:hidden">Excel ({selectedHistoricos.length})</span>
                  </>
                )}
              </button>
            )}

            {/* Botão de impressão em massa */}
            {hasPermission('matriculas.imprimir_historico') && selectedHistoricos.length > 0 && (
              <button
                onClick={imprimirHistoricosMassa}
                disabled={printingMass}
                className="flex items-center justify-center px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {printingMass ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Imprimindo...
                  </>
                ) : (
                  <>
                    <Printer className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Imprimir Selecionados ({selectedHistoricos.length})</span>
                    <span className="sm:hidden">Imprimir ({selectedHistoricos.length})</span>
                  </>
                )}
              </button>
            )}

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Nova Matrícula</span>
              <span className="sm:hidden">Nova</span>
            </button>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      {renderEstatisticas()}

      {/* Filtros */}
      {renderFiltros()}

      {/* Barra de busca */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder="Buscar por nome do aluno, número da matrícula ou curso..."
            value={buscaTemp}
            onChange={(e) => setBuscaTemp(e.target.value)}
            className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Conteúdo principal */}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : matriculas.length === 0 ? (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma matrícula encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filtros.busca || filtros.status || filtros.curso 
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece criando uma nova matrícula.'
            }
          </p>
          {(!filtros.busca && !filtros.status && !filtros.curso) && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Matrícula
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Paginação Superior */}
          {pagination.totalPages > 1 && (
            <>
              {/* Desktop: Componente original */}
              <div className="hidden sm:block mb-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={(page) => atualizarFiltros({ page })}
                  showItemsPerPage={true}
                  onItemsPerPageChange={(limit) => atualizarFiltros({ limit })}
                  className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
                />
              </div>

              {/* Mobile: Versão customizada */}
              <div className="sm:hidden mb-4">
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  {/* Informações de resultados */}
                  <div className="flex flex-col gap-3">
                    <div className="text-xs text-gray-700">
                      Mostrando <span className="font-medium">{((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                      </span>{' '}
                      de <span className="font-medium">{pagination.totalItems}</span> resultados
                    </div>
                    
                    <div className="flex justify-between items-center">
                      {/* Navegação rápida - apenas no mobile quando há muitas páginas */}
                      {pagination.totalPages > 5 ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-700">Ir para:</span>
                          <input
                            type="number"
                            min="1"
                            max={pagination.totalPages}
                            value={pagination.currentPage}
                            onChange={(e) => {
                              const page = parseInt(e.target.value);
                              if (page >= 1 && page <= pagination.totalPages) {
                                atualizarFiltros({ page });
                              }
                            }}
                            className="w-16 text-xs border border-gray-300 rounded px-2 py-1 text-center focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                          />
                          <span className="text-xs text-gray-700">de {pagination.totalPages}</span>
                        </div>
                      ) : (
                        <div></div>
                      )}
                      
                      {/* Controles de itens por página */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-700">Por página:</span>
                        <select
                          value={pagination.itemsPerPage}
                          onChange={(e) => atualizarFiltros({ limit: parseInt(e.target.value), page: 1 })}
                          className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Navegação de páginas */}
                  <div className="flex items-center justify-center mt-3">
                    <div className="flex items-center space-x-1">
                      {/* Página anterior */}
                      <button
                        onClick={() => atualizarFiltros({ page: pagination.currentPage - 1 })}
                        disabled={pagination.currentPage === 1}
                        className="p-2 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Página anterior"
                      >
                        ‹
                      </button>

                      {/* Números das páginas - máximo 3 no mobile */}
                      {(() => {
                        const current = pagination.currentPage;
                        const total = pagination.totalPages;
                        const pages = [];
                        
                        // Mostrar página atual e adjacentes (máximo 3)
                        const start = Math.max(1, current - 1);
                        const end = Math.min(total, current + 1);
                        
                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <button
                              key={i}
                              onClick={() => atualizarFiltros({ page: i })}
                              className={`px-3 py-2 text-xs rounded-md transition-colors ${
                                i === current
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }
                        
                        return pages;
                      })()}

                      {/* Próxima página */}
                      <button
                        onClick={() => atualizarFiltros({ page: pagination.currentPage + 1 })}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="p-2 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Próxima página"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Lista de matrículas - Responsiva */}
          
          {/* Desktop: Tabela */}
          <div className="hidden lg:block bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Checkbox "Selecionar Todos" - Apenas para concluídas */}
                    {hasPermission('matriculas.imprimir_historico') && matriculasElegiveis.length > 0 && (
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          checked={selectedHistoricos.length === matriculasElegiveis.length && matriculasElegiveis.length > 0}
                          onChange={toggleSelectAllHistoricos}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer"
                          title="Selecionar todos os históricos"
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matrícula
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aluno
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Curso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Período
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Matrícula
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {matriculas.map((matricula) => (
                    <tr key={matricula.id} className="hover:bg-gray-50">
                      {/* Checkbox de seleção individual - Apenas para concluídas */}
                      {hasPermission('matriculas.imprimir_historico') && matriculasElegiveis.length > 0 && (
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {(matricula.status === 'Concluída' || matricula.status === 'Concluida') ? (
                            <input
                              type="checkbox"
                              checked={selectedHistoricos.includes(matricula.id)}
                              onChange={() => toggleSelectHistorico(matricula.id)}
                              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded cursor-pointer"
                              title="Selecionar para impressão"
                            />
                          ) : (
                            <span className="w-4 h-4 inline-block" />
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {matricula.numeroMatricula}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-8 w-8 text-gray-400" />
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {matricula.nomeAluno}
                            </div>
                            <div className="text-sm text-gray-500">
                              {matricula.numeroMatriculaPrincipal}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BookOpen className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {matricula.nomeCurso}
                            </div>
                            <div className="text-sm text-gray-500">
                              {matricula.curso?.codigo}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {matricula.tipoCurso || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {matricula.periodo || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatarStatus(matricula.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                          {formatDate(matricula.dataMatricula)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {/* Botão Ver Detalhes */}
                          {hasPermission('matriculas.read') && (
                            <button
                              onClick={() => {
                                setSelectedMatricula(matricula);
                                setShowDetailsModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          )}

                          {/* Botão Ajustar Matrícula */}
                          {hasPermission('matriculas.update') && (
                            <button
                              onClick={() => {
                                setSelectedMatricula(matricula);
                                setShowAjustarMatriculaModal(true);
                              }}
                              className="text-indigo-600 hover:text-indigo-900 transition-colors"
                              title="Ajustar Número de Matrícula"
                            >
                              <Hash className="h-4 w-4" />
                            </button>
                          )}

                          {/* Botão Trancar */}
                          {matricula.status === 'Ativa' && hasPermission('matriculas.trancar') && (
                            <button
                              onClick={() => abrirModalAcao('trancar', matricula)}
                              disabled={actionLoading[matricula.id] === 'trancar'}
                              className="text-orange-600 hover:text-orange-900 transition-colors disabled:opacity-50"
                              title="Trancar matrícula"
                            >
                              {actionLoading[matricula.id] === 'trancar' ? (
                                <span className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                <Pause className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {/* Botão Reativar */}
                          {matricula.status === 'Trancada' && hasPermission('matriculas.reativar') && (
                            <button
                              onClick={() => abrirModalAcao('reativar', matricula)}
                              disabled={actionLoading[matricula.id] === 'reativar'}
                              className="text-green-600 hover:text-green-900 transition-colors disabled:opacity-50"
                              title="Reativar matrícula"
                            >
                              {actionLoading[matricula.id] === 'reativar' ? (
                                <span className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {/* Botão Concluir */}
                          {matricula.status === 'Ativa' && hasPermission('matriculas.concluir') && (
                            <button
                              onClick={() => abrirModalAcao('concluir', matricula)}
                              disabled={actionLoading[matricula.id] === 'concluir'}
                              className="text-blue-600 hover:text-blue-900 transition-colors disabled:opacity-50"
                              title="Concluir matrícula"
                            >
                              {actionLoading[matricula.id] === 'concluir' ? (
                                <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                <CheckCircle className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {/* Botão Cancelar */}
                          {(matricula.status === 'Ativa' || matricula.status === 'Trancada') && hasPermission('matriculas.cancelar') && (
                            <button
                              onClick={() => abrirModalAcao('cancelar', matricula)}
                              disabled={actionLoading[matricula.id] === 'cancelar'}
                              className="text-red-600 hover:text-red-900 transition-colors disabled:opacity-50"
                              title="Cancelar matrícula"
                            >
                              {actionLoading[matricula.id] === 'cancelar' ? (
                                <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </button>
                          )}

                          {/* Botão Imprimir Histórico */}
                          {(matricula.status === 'Concluída' || matricula.status === 'Concluida') && hasPermission('matriculas.imprimir_historico') && (
                            <button
                              onClick={() => imprimirHistorico(matricula)}
                              disabled={actionLoading[`historico_${matricula.id}`]}
                              className="p-2 text-purple-600 hover:text-purple-800 transition-colors disabled:opacity-50"
                              title="Imprimir Histórico Acadêmico"
                            >
                              {actionLoading[`historico_${matricula.id}`] ? (
                                <span className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin inline-block" />
                              ) : (
                                <Printer className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: Cards */}
          <div className="lg:hidden space-y-4">
            {matriculas.map((matricula) => {
              // DEBUG: Log para verificar status
              if (matricula.status === 'Concluida' || matricula.status === 'Concluída') {
                console.log('✅ Matrícula Concluída encontrada:', matricula.id, matricula.nomeAluno, 'Status:', matricula.status);
              }
              
              return (
              <div key={matricula.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Header do card */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-500">#{matricula.numeroMatricula}</span>
                    {formatarStatus(matricula.status)}
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Botão Ver Detalhes */}
                    {hasPermission('matriculas.read') && (
                      <button
                        onClick={() => {
                          setSelectedMatricula(matricula);
                          setShowDetailsModal(true);
                        }}
                        className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}

                    {/* Botão Ajustar Matrícula */}
                    {hasPermission('matriculas.update') && (
                      <button
                        onClick={() => {
                          setSelectedMatricula(matricula);
                          setShowAjustarMatriculaModal(true);
                        }}
                        className="p-1 text-indigo-600 hover:text-indigo-800 transition-colors"
                        title="Ajustar Número de Matrícula"
                      >
                        <Hash className="h-4 w-4" />
                      </button>
                    )}

                    {/* Botão Imprimir Histórico */}
                    {(matricula.status === 'Concluída' || matricula.status === 'Concluida') && hasPermission('matriculas.imprimir_historico') && (
                      <button
                        onClick={() => imprimirHistorico(matricula)}
                        disabled={actionLoading[`historico_${matricula.id}`]}
                        className="p-1 text-purple-600 hover:text-purple-800 transition-colors disabled:opacity-50"
                        title="Imprimir Histórico Acadêmico"
                      >
                        {actionLoading[`historico_${matricula.id}`] ? (
                          <span className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin inline-block" />
                        ) : (
                          <Printer className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    
                    {/* Botões para matrículas Ativas */}
                    {matricula.status === 'Ativa' && (
                      <>
                        {hasPermission('matriculas.trancar') && (
                          <button
                            onClick={() => abrirModalAcao('trancar', matricula)}
                            disabled={actionLoading[matricula.id] === 'trancar'}
                            className="p-1 text-orange-600 hover:text-orange-800 transition-colors disabled:opacity-50"
                            title="Trancar"
                          >
                            {actionLoading[matricula.id] === 'trancar' ? (
                              <span className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin inline-block" />
                            ) : (
                              <Pause className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {hasPermission('matriculas.concluir') && (
                          <button
                            onClick={() => abrirModalAcao('concluir', matricula)}
                            disabled={actionLoading[matricula.id] === 'concluir'}
                            className="p-1 text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
                            title="Concluir"
                          >
                            {actionLoading[matricula.id] === 'concluir' ? (
                              <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin inline-block" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {hasPermission('matriculas.cancelar') && (
                          <button
                            onClick={() => abrirModalAcao('cancelar', matricula)}
                            disabled={actionLoading[matricula.id] === 'cancelar'}
                            className="p-1 text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                            title="Cancelar"
                          >
                            {actionLoading[matricula.id] === 'cancelar' ? (
                              <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin inline-block" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </>
                    )}

                    {/* Botões para matrículas Trancadas */}
                    {matricula.status === 'Trancada' && (
                      <>
                        {hasPermission('matriculas.reativar') && (
                          <button
                            onClick={() => abrirModalAcao('reativar', matricula)}
                            disabled={actionLoading[matricula.id] === 'reativar'}
                            className="p-1 text-green-600 hover:text-green-800 transition-colors disabled:opacity-50"
                            title="Reativar"
                          >
                            {actionLoading[matricula.id] === 'reativar' ? (
                              <span className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin inline-block" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {hasPermission('matriculas.cancelar') && (
                          <button
                            onClick={() => abrirModalAcao('cancelar', matricula)}
                            disabled={actionLoading[matricula.id] === 'cancelar'}
                            className="p-1 text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                            title="Cancelar"
                          >
                            {actionLoading[matricula.id] === 'cancelar' ? (
                              <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin inline-block" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </>
                    )}

                    {/* Botão de Imprimir Histórico - Apenas para matrículas concluídas */}
                    {(matricula.status === 'Concluída' || matricula.status === 'Concluida') && hasPermission('matriculas.imprimir_historico') && (
                      <button
                        onClick={() => imprimirHistorico(matricula)}
                        disabled={actionLoading[`historico_${matricula.id}`]}
                        className="p-1 text-purple-600 hover:text-purple-800 transition-colors disabled:opacity-50"
                        title="Imprimir Histórico Acadêmico"
                      >
                        {actionLoading[`historico_${matricula.id}`] ? (
                          <span className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin inline-block" />
                        ) : (
                          <Printer className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Conteúdo do card */}
                <div className="p-4">
                  <div className="space-y-3">
                    {/* Aluno */}
                    <div className="flex items-start space-x-3">
                      <User className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {matricula.nomeAluno}
                        </p>
                        {matricula.numeroMatriculaPrincipal && (
                          <p className="text-xs text-gray-500">
                            Mat. Principal: {matricula.numeroMatriculaPrincipal}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Curso */}
                    <div className="flex items-start space-x-3">
                      <BookOpen className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {matricula.nomeCurso}
                        </p>
                        {matricula.curso?.codigo && (
                          <p className="text-xs text-gray-500">
                            Código: {matricula.curso.codigo}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Informações adicionais */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500">Tipo</p>
                        <p className="text-xs font-medium text-gray-900">
                          {matricula.tipoCurso || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Período</p>
                        <p className="text-xs font-medium text-gray-900">
                          {matricula.periodo || '-'}
                        </p>
                      </div>
                    </div>

                    {/* Data */}
                    <div className="flex items-center space-x-2 pt-2 border-t border-gray-100">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <p className="text-xs text-gray-500">
                        Matrícula em {formatDate(matricula.dataMatricula)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Paginação Inferior */}
          {pagination.totalPages > 1 && (
            <>
              {/* Desktop: Componente original */}
              <div className="hidden sm:block mt-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={(page) => atualizarFiltros({ page })}
                  showItemsPerPage={false}
                  className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
                />
              </div>

              {/* Mobile: Versão simplificada */}
              <div className="sm:hidden mt-4">
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between">
                    {/* Informação de página atual */}
                    <div className="text-xs text-gray-700">
                      Página {pagination.currentPage} de {pagination.totalPages}
                    </div>

                    {/* Navegação simplificada */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => atualizarFiltros({ page: pagination.currentPage - 1 })}
                        disabled={pagination.currentPage === 1}
                        className="flex items-center px-3 py-2 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded-md"
                      >
                        <span>‹ Anterior</span>
                      </button>

                      <button
                        onClick={() => atualizarFiltros({ page: pagination.currentPage + 1 })}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="flex items-center px-3 py-2 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded-md"
                      >
                        <span>Próxima ›</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Modal de criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-screen overflow-y-auto">
            <MatriculaCreate
              onSave={handleMatriculaSave}
              onCancel={handleMatriculaCancel}
              initialData={null}
            />
          </div>
        </div>
      )}

      {/* Modal de Detalhes da Matrícula */}
      {showDetailsModal && selectedMatricula && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
          <div className="relative top-2 sm:top-10 mx-auto border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-4 sm:mb-6 p-4 sm:p-6 pb-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Detalhes da Matrícula
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {/* Informações da Matrícula */}
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
                    <span className="text-sm sm:text-base">Informações da Matrícula</span>
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Número:</span>
                      <span className="text-xs sm:text-sm text-gray-900 font-mono">{selectedMatricula.numeroMatricula}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:items-center">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full self-start sm:self-auto ${
                        selectedMatricula.status === 'Ativa' ? 'bg-green-100 text-green-800' :
                        selectedMatricula.status === 'Trancada' ? 'bg-yellow-100 text-yellow-800' :
                        selectedMatricula.status === 'Concluída' ? 'bg-blue-100 text-blue-800' :
                        selectedMatricula.status === 'Cancelada' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedMatricula.status}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Data Matrícula:</span>
                      <span className="text-xs sm:text-sm text-gray-900">
                        {formatDate(selectedMatricula.dataMatricula) || 'Não informado'}
                      </span>
                    </div>
                    {selectedMatricula.status === 'Trancada' && selectedMatricula.dataStatusAtual && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Data de Trancamento:</span>
                        <span className="text-xs sm:text-sm text-gray-900">
                          {formatDate(selectedMatricula.dataStatusAtual)}
                        </span>
                      </div>
                    )}
                    {selectedMatricula.status === 'Concluida' && selectedMatricula.dataStatusAtual && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Data de Conclusão:</span>
                        <span className="text-xs sm:text-sm text-gray-900">
                          {formatDate(selectedMatricula.dataStatusAtual)}
                        </span>
                      </div>
                    )}
                    {selectedMatricula.status === 'Cancelada' && selectedMatricula.dataStatusAtual && (
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">Data de Cancelamento:</span>
                        <span className="text-xs sm:text-sm text-gray-900">
                          {formatDate(selectedMatricula.dataStatusAtual)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informações do Aluno */}
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600" />
                    <span className="text-sm sm:text-base">Informações do Aluno</span>
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Nome:</span>
                      <span className="text-xs sm:text-sm text-gray-900 font-medium break-words">{selectedMatricula.nomeAluno}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">CPF:</span>
                      <span className="text-xs sm:text-sm text-gray-900 font-mono">{selectedMatricula.cpfAluno}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">RG:</span>
                      <span className="text-xs sm:text-sm text-gray-900 font-mono">{selectedMatricula.rgAluno}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">E-mail:</span>
                      <span className="text-xs sm:text-sm text-gray-900 break-words">{selectedMatricula.emailAluno}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Telefone:</span>
                      <span className="text-xs sm:text-sm text-gray-900">{selectedMatricula.telefoneAluno}</span>
                    </div>
                  </div>
                </div>

                {/* Informações do Curso */}
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-purple-600" />
                    <span className="text-sm sm:text-base">Informações do Curso</span>
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Nome:</span>
                      <span className="text-xs sm:text-sm text-gray-900 font-medium break-words">{selectedMatricula.nomeCurso}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Tipo de Curso:</span>
                      <span className="text-xs sm:text-sm text-gray-900">{selectedMatricula.tipoCurso || '-'}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Período:</span>
                      <span className="text-xs sm:text-sm text-gray-900">{selectedMatricula.periodo}</span>
                    </div>
                  </div>
                </div>

                {/* Informações Adicionais */}
                <div className="bg-gray-50 p-4 sm:p-6 rounded-lg">
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-orange-600" />
                    <span className="text-sm sm:text-base">Informações Adicionais</span>
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Extensão:</span>
                      <span className="text-xs sm:text-sm text-gray-900 break-words">{selectedMatricula.nomeExtensao || 'Não informado'}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-xs sm:text-sm font-medium text-gray-500">Coordenador:</span>
                      <span className="text-xs sm:text-sm text-gray-900 break-words">{selectedMatricula.nomeCoordenador || 'Não informado'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Observações */}
              {selectedMatricula.observacoes && (
                <div className="mt-4 sm:mt-6 bg-blue-50 p-3 sm:p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2 sm:mb-3">Observações:</h4>
                  <div className="text-xs sm:text-sm text-gray-700 space-y-2">
                    {selectedMatricula.observacoes.split('\n').map((linha, index) => (
                      linha.trim() && (
                        <div key={index} className="p-2 bg-white rounded border-l-4 border-blue-400">
                          {linha.trim()}
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Botões de ação - Fixed Bottom */}
            <div className="flex-shrink-0 border-t bg-gray-50 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
                <div className="flex flex-wrap gap-2">
                  {/* Botões para matrículas Ativas */}
                  {selectedMatricula.status === 'Ativa' && (
                    <>
                      {hasPermission('matriculas.trancar') && (
                        <button
                          onClick={() => {
                            setShowDetailsModal(false);
                            abrirModalAcao('trancar', selectedMatricula);
                          }}
                          className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                        >
                          <Pause className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Trancar
                        </button>
                      )}
                      {hasPermission('matriculas.concluir') && (
                        <button
                          onClick={() => {
                            setShowDetailsModal(false);
                            abrirModalAcao('concluir', selectedMatricula);
                          }}
                          className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Concluir
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Botão para matrículas Trancadas */}
                  {selectedMatricula.status === 'Trancada' && hasPermission('matriculas.reativar') && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        abrirModalAcao('reativar', selectedMatricula);
                      }}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Reativar
                    </button>
                  )}
                  
                  {/* Botão Cancelar para Ativas ou Trancadas */}
                  {(selectedMatricula.status === 'Ativa' || selectedMatricula.status === 'Trancada') && hasPermission('matriculas.cancelar') && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        abrirModalAcao('cancelar', selectedMatricula);
                      }}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      Cancelar
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ações */}
      {showActionModal && selectedMatricula && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-4">
          <div className="relative top-10 sm:top-20 mx-auto border w-full max-w-md shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-4 p-4 sm:p-5 pb-0">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 capitalize">
                  {actionType} Matrícula
                </h3>
                <button
                  onClick={fecharModalAcao}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-5">
              <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-md">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">
                  <span className="font-medium">Matrícula:</span> {selectedMatricula.numeroMatricula}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 mb-1">
                  <span className="font-medium">Aluno:</span> {selectedMatricula.nomeAluno}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Curso:</span> {selectedMatricula.nomeCurso}
                </p>
              </div>

              {/* Formulário baseado na ação */}
              <div className="mb-4 sm:mb-6">
                {actionType === 'trancar' && (
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data de trancamento *
                      </label>
                      <input
                        type="date"
                        value={actionData.dataTrancamento || ''}
                        onChange={(e) => setActionData({...actionData, dataTrancamento: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Observações do trancamento *
                      </label>
                      <textarea
                        value={actionData.motivoTrancamento || ''}
                        onChange={(e) => setActionData({...actionData, motivoTrancamento: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="Descreva o motivo do trancamento..."
                        required
                      />
                    </div>
                  </div>
                )}

                {actionType === 'reativar' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo da reativação *
                    </label>
                    <textarea
                      value={actionData.motivoReativacao || ''}
                      onChange={(e) => setActionData({...actionData, motivoReativacao: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="Descreva o motivo da reativação..."
                      required
                    />
                  </div>
                )}

                {actionType === 'concluir' && (
                  <div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data de conclusão *
                      </label>
                      <input
                        type="date"
                        value={actionData.dataConclusao || ''}
                        onChange={(e) => setActionData({...actionData, dataConclusao: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Observações
                      </label>
                      <textarea
                        value={actionData.observacoesConclusao || ''}
                        onChange={(e) => setActionData({...actionData, observacoesConclusao: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="Observações sobre a conclusão..."
                      />
                    </div>
                  </div>
                )}

                {actionType === 'cancelar' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo do cancelamento *
                    </label>
                    <textarea
                      value={actionData.motivoCancelamento || ''}
                      onChange={(e) => setActionData({...actionData, motivoCancelamento: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows="3"
                      placeholder="Descreva o motivo do cancelamento..."
                      required
                    />
                  </div>
                )}
              </div>

              {/* Botões de ação com layout responsivo */}
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={fecharModalAcao}
                  className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    // Mapear campos específicos para cada ação
                    let dadosMapeados = { ...actionData };
                    
                    if (actionType === 'concluir') {
                      dadosMapeados = {
                        dataConclusao: actionData.dataConclusao,
                        observacoes: actionData.observacoesConclusao
                      };
                    } else if (actionType === 'reativar') {
                      dadosMapeados = {
                        dataRetorno: actionData.dataRetorno,
                        observacoes: actionData.motivoReativacao
                      };
                    }
                    // Para trancamento, usamos os dados diretos (motivoTrancamento já está correto)
                    
                    executarAcao(actionType, selectedMatricula.id, dadosMapeados);
                  }}
                  disabled={
                    actionLoading[selectedMatricula.id] === actionType ||
                    (actionType === 'trancar' && (!actionData.dataTrancamento || !actionData.motivoTrancamento)) ||
                    (actionType === 'reativar' && !actionData.motivoReativacao) ||
                    (actionType === 'concluir' && !actionData.dataConclusao) ||
                    (actionType === 'cancelar' && !actionData.motivoCancelamento)
                  }
                  className={`w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                    actionType === 'trancar' ? 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500' :
                    actionType === 'reativar' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' :
                    actionType === 'concluir' ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' :
                    'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {actionLoading[selectedMatricula.id] === actionType ? (
                    <span className="flex items-center justify-center">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      <span className="text-xs sm:text-sm">Processando...</span>
                    </span>
                  ) : (
                    <span className="text-xs sm:text-sm">{`Confirmar ${actionType}`}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Erro com z-index alto */}
      <ModalError 
        show={showErrorModal}
        error={error}
        onClose={fecharModalErro}
      />

      {/* Modal de Ajustar Matrícula */}
      <AjustarMatriculaModal
        isOpen={showAjustarMatriculaModal}
        onClose={() => {
          setShowAjustarMatriculaModal(false);
          setSelectedMatricula(null);
        }}
        matricula={selectedMatricula}
        onConfirm={handleAjustarMatricula}
      />
    </div>
  );
};

export default MatriculasList;